import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, ScrollText, ShieldCheck, LogIn, Archive, Trash2, RotateCcw, Building2, Layers, UserCog,
} from "lucide-react";
import { sr } from "../../lib/sr";

const ACTION_META = {
  sr_admin_act_as: { label: "Acted as client", icon: LogIn, color: "var(--blue)" },
  sr_admin_create_client: { label: "Created client", icon: Building2, color: "#067647" },
  sr_admin_reassign_owner: { label: "Reassigned owner", icon: UserCog, color: "#9A7412" },
  sr_admin_assign_manager: { label: "Assigned manager", icon: UserCog, color: "#9A7412" },
  sr_admin_execution_mode: { label: "Changed execution mode", icon: RotateCcw, color: "var(--blue)" },
  sr_admin_campaign_archive: { label: "Archived campaign", icon: Archive, color: "#9A7412" },
  sr_admin_campaign_delete: { label: "Deleted campaign", icon: Trash2, color: "#C0362C" },
  sr_admin_campaign_restore: { label: "Restored campaign", icon: RotateCcw, color: "var(--blue)" },
  sr_admin_campaign_bulk_archive: { label: "Bulk archived", icon: Layers, color: "#9A7412" },
  sr_admin_campaign_bulk_delete: { label: "Bulk deleted", icon: Layers, color: "#C0362C" },
  sr_admin_undo_archive: { label: "Undid archive", icon: RotateCcw, color: "var(--blue)" },
};

const UNDOABLE = new Set(["sr_admin_campaign_archive", "sr_admin_campaign_bulk_archive"]);

const fmt = (ts) => {
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
};

export default function AdminActivity() {
  const [data, setData] = useState(null);
  const [undoing, setUndoing] = useState(null);
  const [fAdmin, setFAdmin] = useState("");
  const [fClient, setFClient] = useState("");
  const [fAction, setFAction] = useState("");
  const load = () => sr.adminAudit().then(setData).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);

  const undo = async (e) => {
    if (!window.confirm("Restore the campaign(s) archived by this action?")) return;
    setUndoing(e.id);
    try {
      const r = await sr.adminUndoActivity(e.id);
      toast.success(`Restored ${r.restored} campaign(s)`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not undo"); }
    finally { setUndoing(null); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load activity. Admin access is required.</p></div>;

  const allEntries = data.entries || [];
  const admins = [...new Set(allEntries.map((e) => e.user_email).filter(Boolean))].sort();
  const clients = [...new Set(allEntries.map((e) => (e.details || {}).tenant_name).filter(Boolean))].sort();
  const actions = [...new Set(allEntries.map((e) => e.action).filter(Boolean))].sort();
  const actionLabel = (a) => (ACTION_META[a]?.label) || a.replace(/^sr_admin_/, "").replace(/_/g, " ");
  const entries = allEntries.filter((e) =>
    (!fAdmin || e.user_email === fAdmin) &&
    (!fClient || (e.details || {}).tenant_name === fClient) &&
    (!fAction || e.action === fAction));
  const clearFilters = () => { setFAdmin(""); setFClient(""); setFAction(""); };
  const hasFilters = fAdmin || fClient || fAction;

  return (
    <div data-testid="admin-activity-page">
      <div style={{ marginBottom: 16 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><ShieldCheck size={12} /> Admin · Activity</span>
        <h1 style={{ fontSize: 30 }}>Admin activity log</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Who managed which client and what they changed — for accountability. Archives can be undone here.</p>
      </div>

      <div className="sr-card" style={{ marginBottom: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }} data-testid="activity-filters">
        <select className="sr-select" value={fAdmin} onChange={(e) => setFAdmin(e.target.value)} data-testid="filter-admin" style={{ maxWidth: 220 }}>
          <option value="">All admins</option>
          {admins.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="sr-select" value={fClient} onChange={(e) => setFClient(e.target.value)} data-testid="filter-client" style={{ maxWidth: 220 }}>
          <option value="">All clients</option>
          {clients.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="sr-select" value={fAction} onChange={(e) => setFAction(e.target.value)} data-testid="filter-action" style={{ maxWidth: 220 }}>
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}
        </select>
        {hasFilters && <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={clearFilters} data-testid="clear-filters">Clear</button>}
        <span className="sr-badge sr-badge-gray" style={{ marginLeft: "auto" }}>{entries.length} of {allEntries.length}</span>
      </div>

      {entries.length === 0 ? (
        <div className="sr-card" data-testid="admin-activity-empty">
          <p className="sr-muted">{hasFilters ? "No activity matches these filters." : "No admin activity yet. Acting as a client, creating clients, or archiving/deleting campaigns will show up here."}</p>
        </div>
      ) : (
        <div className="sr-card" style={{ overflowX: "auto" }}>
          <table className="sr-table" data-testid="admin-activity-table">
            <thead><tr><th>When</th><th>Admin</th><th>Action</th><th>Client</th><th>Details</th><th className="center">Undo</th></tr></thead>
            <tbody>
              {entries.map((e) => {
                const meta = ACTION_META[e.action] || { label: (e.action || "").replace(/^sr_admin_/, "").replace(/_/g, " "), icon: ScrollText, color: "var(--muted)" };
                const d = e.details || {};
                const extra = [
                  d.name ? `“${d.name}”` : null,
                  d.count != null ? `${d.count} campaign(s)` : null,
                  d.permanent ? "permanent" : null,
                  d.new_owner ? `→ ${d.new_owner}` : null,
                  (d.from && d.to) ? `${d.from} → ${d.to}` : null,
                  d.owner_email ? `owner ${d.owner_email}` : null,
                  d.restored != null ? `${d.restored} restored` : null,
                  d.impersonating ? "while impersonating" : null,
                ].filter(Boolean).join(" · ");
                const canUndo = UNDOABLE.has(e.action) && !d.permanent && !d.undone_at;
                return (
                  <tr key={e.id} data-testid={`activity-row-${e.id}`}>
                    <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmt(e.timestamp)}</td>
                    <td style={{ fontSize: 13 }}>{e.user_email || "—"}</td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: meta.color, fontWeight: 700, fontSize: 13 }}>
                        <meta.icon size={14} /> {meta.label}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>{d.tenant_name || "—"}</td>
                    <td className="sr-muted" style={{ fontSize: 12 }}>{extra || "—"}</td>
                    <td className="center">
                      {canUndo ? (
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => undo(e)} disabled={undoing === e.id} data-testid={`undo-${e.id}`}>
                          {undoing === e.id ? <Loader2 size={13} className="sr-spin" /> : <RotateCcw size={13} />} Undo
                        </button>
                      ) : d.undone_at ? (
                        <span className="sr-badge sr-badge-gray" data-testid={`undone-${e.id}`}>Undone</span>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

